import { Helmet } from 'react-helmet-async';
import { MainNavigation } from '@/components/layout/MainNavigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, Shield, Users, TrendingUp, Clock, Target, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ForBrokers = () => {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: <Shield className="h-6 w-6 text-primary" />,
      title: "Secure Client Management",
      description: "Keep full control of your client relationships with our secure platform. No sales rep takeovers."
    },
    {
      icon: <Users className="h-6 w-6 text-primary" />,
      title: "Complete Market Access",
      description: "Access all 523 PEOs in the market, not just the 5 you typically evaluate through networking."
    },
    {
      icon: <Clock className="h-6 w-6 text-primary" />,
      title: "Streamlined Process",
      description: "Reduce client onboarding from 6-8 weeks to 1 week with automated document collection."
    },
    {
      icon: <Target className="h-6 w-6 text-primary" />,
      title: "Quality Matching",
      description: "Match clients based on actual fit criteria, not PEO marketing materials or sales pitches."
    }
  ];

  const problems = [
    "Sales reps leave their PEO within 2 years on average",
    "Limited access to only 5 PEOs through networking",
    "Evaluating less than 1% of the available market",
    "Decisions based on marketing, not actual fit",
    "Manual processes taking 6-8 weeks",
    "Risk of losing client relationships to PEO sales teams"
  ];

  const features = [
    "Branded broker dashboard for client management",
    "Secure document collection and storage",
    "Automated PEO matching based on client needs",
    "Direct communication channels with clients",
    "Referral tracking and commission management",
    "White-label client experience"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>For Brokers - Run Your PEO Business Securely | The PEO App</title>
        <meta name="description" content="Secure PEO broker platform for Employee Benefits, Workers Comp, and PEO brokers. Access all 523 PEOs, maintain client relationships, streamline your process from 6-8 weeks to 1 week." />
        <meta name="keywords" content="PEO broker platform, employee benefits broker, workers comp broker, secure PEO software, broker client management, PEO referral system" />
      </Helmet>

      <MainNavigation />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-primary/5 via-background to-primary/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <Badge variant="outline" className="mb-6 px-4 py-2">
                For PEO, Employee Benefits & Workers Comp Brokers
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
                Run Your PEO Business
                <span className="text-primary block">Securely</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                Keep your client relationships secure while accessing all 523 PEOs in the market. 
                No more sales rep takeovers. No more limited options.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  onClick={() => navigate('/signup')}
                  className="px-8 py-4 text-lg"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={() => navigate('/how-it-works')}
                  className="px-8 py-4 text-lg"
                >
                  See How It Works
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Problem/Solution Section */}
        <section className="py-20 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  The Broker's Dilemma
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Traditional PEO brokering puts your client relationships at risk and limits your market reach.
                </p>
                <ul className="space-y-4">
                  {problems.map((problem, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 bg-red-500 rounded-full" />
                      </div>
                      <span className="text-foreground">{problem}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-primary/5 to-primary/10 p-8 rounded-2xl">
                <h3 className="text-2xl font-bold mb-6 text-center">
                  <span className="text-primary">523</span> PEOs Available
                </h3>
                <div className="text-center mb-6">
                  <div className="text-sm text-muted-foreground mb-2">Traditional Broker Evaluation</div>
                  <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                    <div className="bg-red-500 h-4 rounded-full" style={{ width: '1%' }}></div>
                  </div>
                  <div className="text-sm font-medium">5 PEOs (&lt;1% of market)</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-2">The PEO App Coverage</div>
                  <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                    <div className="bg-primary h-4 rounded-full" style={{ width: '100%' }}></div>
                  </div>
                  <div className="text-sm font-medium">523 PEOs (100% of market)</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-br from-background to-primary/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Keep Your Clients,
                <span className="text-primary"> Expand Your Market</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Our secure platform ensures you maintain control while providing better outcomes for your clients.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {benefits.map((benefit, index) => (
                <Card key={index} className="p-6 h-full">
                  <CardContent className="p-0">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        {benefit.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                        <p className="text-muted-foreground">{benefit.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Built for Broker Success
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Everything you need to run a modern, efficient PEO brokerage business.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3 p-4 rounded-lg border bg-card">
                  <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Time Comparison */}
        <section className="py-20 bg-gradient-to-br from-primary/5 to-background">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                From 6-8 Weeks to 1 Week
              </h2>
              <p className="text-xl text-muted-foreground">
                Streamline your process without sacrificing quality or control.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="p-8 border-2 border-red-200">
                <CardContent className="p-0 text-center">
                  <div className="text-red-500 text-4xl font-bold mb-4">6-8 Weeks</div>
                  <h3 className="text-xl font-semibold mb-4">Traditional Process</h3>
                  <ul className="text-left space-y-2 text-muted-foreground">
                    <li>• Manual document collection</li>
                    <li>• Limited PEO research</li>
                    <li>• Back-and-forth communications</li>
                    <li>• Manual quote comparisons</li>
                    <li>• Risk of client handoff</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="p-8 border-2 border-primary">
                <CardContent className="p-0 text-center">
                  <div className="text-primary text-4xl font-bold mb-4">1 Week</div>
                  <h3 className="text-xl font-semibold mb-4">The PEO App Process</h3>
                  <ul className="text-left space-y-2 text-muted-foreground">
                    <li>• Automated document collection</li>
                    <li>• AI-powered PEO matching</li>
                    <li>• Streamlined communications</li>
                    <li>• Instant quote comparisons</li>
                    <li>• You maintain client control</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-primary text-primary-foreground">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Secure Your Broker Business?
            </h2>
            <p className="text-xl mb-8 text-primary-foreground/90">
              Join forward-thinking brokers who are protecting their client relationships 
              while expanding their market reach.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                variant="secondary"
                onClick={() => navigate('/signup')}
                className="px-8 py-4 text-lg"
              >
                Start Your Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => navigate('/partnerships')}
                className="px-8 py-4 text-lg border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10"
              >
                Learn About Partnerships
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ForBrokers;